import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from "react";
import { useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import type { Post, PostMedia, PostPage } from "@/shared/contracts";
import { env } from "@/shared/config/env";
import { newIdempotencyKey } from "@/shared/api/idempotency";
import { toApiError } from "@/shared/api/errors";
import { queryKeys } from "@/shared/query/queryClient";
import { useServices } from "@/shared/services/registry";
import { Button } from "@/shared/ui";

/**
 * 朋友圈一条动态的照片（SOCIAL-MULTIPHOTO-117；I 10-16 派单，c84a 复核 CR-4 升成 117 必须件）。
 * 基本四样：
 * - 数量和顺序对：post.media 有几张出几格，顺序就是数组顺序（契约没有封面、没有排序字段），前端不另设上限；
 * - 能逐张看完整图：读到的格子盖一颗「放大查看第 i 张（共 n 张）」，点开是同页弹层（showModal），
 *   上一张、下一张两颗按钮，←→ 键，左右轻扫都能切，标题条写「第 i / n 张」，图整张放下不裁；
 * - 某张读不出来不遮正文：正文本来在照片上面；读不出来的那一格照实说一句（原来那句），别的格照常，弹层里那一张也照实说；
 * - 返回时动态不丢滚动位置：看大图不跳路由；关上时焦点回到那一张的小格（不滚动），页面滚动在弹层开着时锁住、关上还原。
 * 宫格：1 张整幅、完整不裁；2 张并列；4 张二乘二；3 张、5–9 张三列等距方格。格子 1:1、偏上取景（50% 30%）：
 * 动态照片没有脸的框，偏上少切到脸，这是经验值，不是数据。
 * 每张的读取照 UI053 原来那套（PostMediaResource：读取中、读到、读不出；演示、不支持的类型、没有地址三种照实说的话；按观看者和这张换 key），一格一个。
 * 公开页（/world、居民主页）照旧不画 Post.media，这个组件只给朋友圈、动态详情、宠物主页用。
 * 玩家界面不写 AI 字样：media.generated 只留给运营后台（UI-NO-AI-LABEL-01）。
 * 多张时格子小（320 宽三列一格约 82px）：状态那几句只露短的那几个字，整句照旧给读屏（评审 R1 阻断：长句折出孤字、字一放大就被格子裁掉）。
 * 单张读取中按记住的比例留高（这一张以前读到过的话），从动态详情回来时版面不漂（评审 R1 高：先按 16:10 留位，读到以后按真比例变高变矮，返回的滚动位置对不上）。
 * 比例按 media_id 记（评审 R2：图片地址要是带每次都变的签名，按地址记就落空）；按比例留高时不再垫最小高（评审 R2 中：比 3.3:1 还宽的全景图被 96px 托着，返回还会漂一点）。
 */
type Viewer = { userId: string | null; homeId: string | null; petId: string | null };
/** 投影给发帖宠物所在家庭管理员的动态多带一项（PV MEDIA-UNPUBLISH-API-117；契约生成物等 I 重生，先按附加字段读）：能不能让这条动态里的照片不再公开 */
type UnpublishablePost = Post & { viewer_can_unpublish?: boolean };
/** 读到过的那几张的宽高比（按 media_id 记，整页会话里有效）：单张读取中照它留高 */
const seenRatio = new Map<string, number>();
type TileState = "loading" | "loaded" | "error";

/** 撤了以后用回来的那条动态换掉缓存里的（动态详情、朋友圈、宠物主页三处），再全部重读一遍对齐 */
function applyPost(client: QueryClient, next: Post) {
  client.setQueryData(queryKeys.post(next.post_id), next);
  const swap = (page: PostPage | undefined) => (page ? { ...page, items: page.items.map((item) => (item.post_id === next.post_id ? next : item)) } : page);
  client.setQueryData<PostPage>(queryKeys.circleFeed, swap);
  client.setQueriesData<PostPage>({ queryKey: ["social", "pet-posts"] }, swap);
  void client.invalidateQueries({ queryKey: ["social"] });
}

/**
 * 不再公开这一张（SOCIAL-PHOTO-REVOKE-UI-117，I 10-17）：只给发帖宠物所在家庭的管理员（投影给的 viewer_can_unpublish；共同照顾者、别人都没有，不出这一块）。
 * 先确认一次，说清别人就看不到这张了、文字还在；同一张重试用同一个操作号（幂等）。撤了以后用回来的那条动态换掉缓存，这张从动态里拿掉，正文照出。
 * 已经撤过的再撤后端也答成功（幂等）。被拒照实说：403 不是管理员；404 这一张已经不在这条动态里了（重读）；409 有变动（重读）；别的（没连上、服务出错）可以再试一次。字面不写授权、代签、公开依据。
 */
function UnpublishPhoto({ post, media, onDone }: { post: Post; media: PostMedia; onDone: () => void }) {
  const { social } = useServices();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const operation = useRef<string | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const run = useMutation({
    mutationFn: () => {
      operation.current ??= newIdempotencyKey("unpublish-photo");
      return social.unpublishPhoto!(post.post_id, media.media_id, operation.current);
    },
    onSuccess: (result) => {
      // 回来的那条动态换进缓存；看不到了（null）就只重读
      if (result.post) applyPost(queryClient, result.post);
      else void queryClient.invalidateQueries({ queryKey: ["social"] });
      onDone();
    },
    onError: (error) => {
      const status = toApiError(error).status;
      if (status === 404 || status === 409) void queryClient.invalidateQueries({ queryKey: ["social"] });
    },
  });
  if (!social.unpublishPhoto) return null;
  const status = run.error ? toApiError(run.error).status : null;
  const said = !run.error ? null
    : status === 403 ? "只有家里的管理员能改这一项。"
      : status === 404 ? "这一张已经不在这条动态里了。"
        : status === 409 ? "刚才有变动，已经重新读过，再看一眼。"
          : "这一下没改成，可以再试一次。";
  const again = Boolean(run.error) && status !== 403 && status !== 404 && status !== 409;
  return <div ref={boxRef} className="ps-post-viewer__manage">
    <Button size="sm" variant="secondary" data-unpublish="" aria-expanded={confirming} onClick={() => setConfirming((v) => !v)}>不再公开这张照片</Button>
    {confirming ? <span className="ps-post-viewer__confirm" role="group" aria-label="确认不再公开">
      <span className="ps-post-viewer__confirm-text">别人就看不到这张了，这条动态的文字还在。</span>
      <Button size="sm" variant="danger" loading={run.isPending} disabled={status === 403 || status === 404} onClick={() => run.mutate()}>{again ? "再试一次" : "不再公开"}</Button>
      <Button size="sm" variant="secondary" disabled={run.isPending} onClick={() => { boxRef.current?.querySelector<HTMLButtonElement>("[data-unpublish]")?.focus(); setConfirming(false); }}>先不了</Button>
    </span> : null}
    {said ? <p className="ps-post-viewer__manage-error" role="alert">{said}</p> : null}
  </div>;
}

export function usablePostMediaUrl(url: string | null): boolean {
  if (!url || url.trim() !== url || /[\u0000- \u007f]/.test(url)) return false;
  try { const parsed = new URL(url, window.location.href); return parsed.protocol === "http:" || parsed.protocol === "https:"; } catch { return false; }
}

/** 这一张能不能出图：演示、不支持的类型、没有地址都照实说一句 */
function unavailableText(media: PostMedia, demo: boolean): string | null {
  if (demo) return "演示动态未提供实际配图。";
  if (media.kind !== "image") return "这类配图暂不支持显示。";
  if (!usablePostMediaUrl(media.url)) return "暂时没有可读取的配图。";
  return null;
}
/** 多张宫格的小格子里露出来的短说法（整句给读屏） */
const SHORT: Record<string, string> = { "演示动态未提供实际配图。": "演示无图", "这类配图暂不支持显示。": "暂不支持", "暂时没有可读取的配图。": "没有配图" };
/** 读取中、读不出来两句：小格子里只露中间那几个字，前后藏起来给读屏，整句的字还是原来那句（UI053 的说法不变） */
const split = (lead: string, shown: string, tail: string) => <><span className="visually-hidden">{lead}</span>{shown}<span className="visually-hidden">{tail}</span></>;

/** 宫格几列：1 张一列，2 张、4 张两列，其余三列 */
export function photoColumns(count: number): number {
  return count <= 1 ? 1 : count === 2 || count === 4 ? 2 : 3;
}

/** 一格（照 UI053 的 PostMediaResource）：只管这一张的读取状态，读到了才给「放大查看」；状态报给宫格，弹层里照它说 */
function PostMediaResource({ media, demo, compact, label, onState, onOpen, openRef }: {
  media: PostMedia; demo: boolean; compact: boolean; label: string;
  onState: (state: TileState) => void; onOpen: () => void; openRef: (node: HTMLButtonElement | null) => void;
}) {
  const [state, setState] = useState<TileState>("loading");
  const active = useRef(true), image = useRef<HTMLImageElement>(null);
  const report = useRef(onState);
  report.current = onState;
  useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
  useEffect(() => { report.current(state); }, [state]);
  const text = unavailableText(media, demo);
  if (text) return <div className="ps-post__unavailable053"><p>{compact ? <><span aria-hidden="true">{SHORT[text]}</span><span className="visually-hidden">{text}</span></> : text}</p></div>;
  const current = (target: HTMLImageElement) => active.current && target === image.current && target.isConnected;
  // 单张读取中：这一张以前读到过就按那时的比例留高（宽 100%、最高 420，和读到以后一样大）
  const ratio = !compact && state === "loading" ? seenRatio.get(media.media_id) : undefined;
  return <figure className={`ps-post__figure ps-post__figure053 is-${state}`} data-media-id={media.media_id}>
    {state !== "error" ? <img ref={image} src={media.url!} alt={state === "loaded" ? media.alt ?? "动态配图" : ""} aria-hidden={state !== "loaded"} loading="lazy" style={ratio ? { aspectRatio: String(ratio), minHeight: 0 } : undefined}
      onLoad={event => { const img = event.currentTarget; if (!current(img)) return; if (img.naturalWidth && img.naturalHeight) seenRatio.set(media.media_id, img.naturalWidth / img.naturalHeight); setState("loaded"); }}
      onError={event => { if (current(event.currentTarget)) setState("error"); }} /> : null}
    {state === "loading" ? <p className="ps-post__read-state053" role="status">{compact ? split("配图", "读取中", "…") : "配图读取中…"}</p> : null}
    {state === "error" ? <p className="ps-post__read-state053" role="status">{compact ? split("配图暂时", "无法读取", "，正文仍可阅读。") : "配图暂时无法读取，正文仍可阅读。"}</p> : null}
    {state === "loaded" ? <button ref={openRef} type="button" className="ps-post__photo-open" aria-label={label} onClick={onOpen} /> : null}
  </figure>;
}

type SwipeHandlers = { onPointerDown: (event: ReactPointerEvent) => void; onPointerUp: (event: ReactPointerEvent) => void };
/** 左右轻扫换一张：横向挪 ≥40px、比竖向多才算（往左扫看下一张） */
function useSwipe(onSwipe: (dir: -1 | 1) => void): SwipeHandlers {
  const start = useRef<{ x: number; y: number } | null>(null);
  const latest = useRef(onSwipe);
  latest.current = onSwipe;
  return {
    onPointerDown: (event) => { start.current = { x: event.clientX, y: event.clientY }; },
    onPointerUp: (event) => {
      const from = start.current; start.current = null;
      if (!from) return;
      const dx = event.clientX - from.x, dy = event.clientY - from.y;
      if (Math.abs(dx) >= 40 && Math.abs(dx) > Math.abs(dy)) latest.current(dx < 0 ? 1 : -1);
    },
  };
}

export function PostPhotos({ post, viewer }: { post: Post; viewer: Viewer }) {
  const media = post.media;
  const n = media.length;
  const demo = env.dataMode !== "live" || post.data_origin === "fixture";
  const [states, setStates] = useState<Record<number, TileState>>({});
  const [picked, setViewing] = useState<number | null>(null);
  // 看着的时候这条动态的照片变少了（撤掉一张）：停在还在的最后一张，一张都没了就关上（弹层不拆，状态随后跟上）
  const viewing = picked === null || !n ? null : Math.min(picked, n - 1);
  const opens = useRef<(HTMLButtonElement | null)[]>([]);
  const dialogRef = useRef<HTMLDialogElement>(null);
  // 关上时要回到哪一张：最后看的那一张（关的那一下 viewing 已经是 null）
  const lastViewed = useRef(0);
  if (viewing !== null) lastViewed.current = viewing;
  const go = (to: number) => { if (to >= 0 && to < n) setViewing(to); };
  const swipe = useSwipe(dir => go((viewing ?? 0) + dir));
  const open = viewing !== null;
  // 不再公开一张以后的那一句（SOCIAL-PHOTO-REVOKE-UI-117）：读屏会念，焦点也落在这一句上
  const [notice, setNotice] = useState<string | null>(null);
  const noticeRef = useRef<HTMLParagraphElement>(null);
  const canUnpublish = (post as UnpublishablePost).viewer_can_unpublish === true;
  const unpublished = () => {
    setNotice("这张照片不再公开了，这条动态的文字还在。");
    setViewing(null);
    // 焦点一律落在这一句上（读屏念出来）：剩下的小格要按新的那条动态重新读，「放大查看」那几颗还没出来，关弹层那一段还不回去
    setTimeout(() => noticeRef.current?.focus({ preventScroll: true }), 0);
  };
  useEffect(() => { if (picked !== viewing) setViewing(viewing); }, [picked, viewing]);
  // 按到头那颗置灰了、焦点还停在它上面：挪到另一颗，免得键盘用户停在点不了的按钮上
  useEffect(() => {
    const dialog = dialogRef.current, active = document.activeElement;
    if (dialog && active instanceof HTMLButtonElement && active.disabled && dialog.contains(active)) dialog.querySelector<HTMLButtonElement>(".ps-post-viewer__step:not(:disabled)")?.focus();
  }, [viewing]);
  useLayoutEffect(() => {
    const dialog = dialogRef.current;
    if (!open || !dialog) return;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    if (typeof dialog.showModal === "function" && !dialog.open) dialog.showModal();
    return () => {
      const heldFocus = dialog.contains(document.activeElement) || document.activeElement === document.body;
      if (dialog.open && typeof dialog.close === "function") dialog.close();
      document.body.style.overflow = overflow;
      // 焦点回到最后看的那一张的小格；那一张没有「放大查看」（读不出来）就回到离它最近的一张，不滚动：动态停在原位
      if (heldFocus) queueMicrotask(() => {
        const at = lastViewed.current;
        const order = opens.current.map((_, i) => i).sort((a, b) => Math.abs(a - at) - Math.abs(b - at));
        order.map(i => opens.current[i]).find(node => node?.isConnected)?.focus({ preventScroll: true });
      });
    };
  }, [open]);
  const said = notice ? <p ref={noticeRef} className="ps-post__photo-notice" role="status" tabIndex={-1}>{notice}</p> : null;
  if (!n) return said;
  const author = post.author.display_name;
  return <>
    <div className="ps-post__photos" data-count={n} style={{ "--photo-cols": photoColumns(n) } as CSSProperties}>
      {media.map((item, index) => <PostMediaResource
        key={JSON.stringify([viewer.userId, viewer.homeId, viewer.petId, post.post_id, item.media_id, item.url, item.kind, item.generated, post.data_origin, env.dataMode, index])}
        media={item} demo={demo} compact={n > 1}
        label={n > 1 ? `放大查看第 ${index + 1} 张（共 ${n} 张）：${item.alt ?? "动态配图"}` : `放大查看这张照片：${item.alt ?? "动态配图"}`}
        onState={state => setStates(prev => (prev[index] === state ? prev : { ...prev, [index]: state }))}
        onOpen={() => setViewing(index)} openRef={node => { opens.current[index] = node; }} />)}
    </div>
    {said}
    {viewing !== null ? <dialog ref={dialogRef} className="ps-post-viewer" aria-label={`${author}的动态照片`}
      onCancel={event => { event.preventDefault(); setViewing(null); }}
      onKeyDown={event => {
        if (event.key === "ArrowLeft") { event.preventDefault(); go(viewing - 1); }
        else if (event.key === "ArrowRight") { event.preventDefault(); go(viewing + 1); }
      }}>
      <div className="ps-post-viewer__bar">
        <span className="ps-post-viewer__count" aria-live="polite">{n > 1 ? `第 ${viewing + 1} / ${n} 张` : "这张照片"}</span>
        <button type="button" className="ps-post-viewer__close" autoFocus aria-label="关上照片" onClick={() => setViewing(null)}>×</button>
      </div>
      <ViewerStage media={media[viewing]!} demo={demo} many={n > 1} state={states[viewing]} swipe={swipe} />
      {n > 1 ? <div className="ps-post-viewer__nav">
        <button type="button" className="ps-post-viewer__step" aria-label="上一张" disabled={viewing === 0} onClick={() => go(viewing - 1)}>‹</button>
        <button type="button" className="ps-post-viewer__step" aria-label="下一张" disabled={viewing === n - 1} onClick={() => go(viewing + 1)}>›</button>
      </div> : null}
      {canUnpublish ? <UnpublishPhoto key={media[viewing]!.media_id} post={post} media={media[viewing]!} onDone={unpublished} /> : null}
    </dialog> : null}
  </>;
}

/** 弹层里的那一张：读到的整张放下；读不出来、演示、不支持、没有地址都照实说（数量和顺序和宫格一致，不跳过） */
function ViewerStage({ media, demo, many, state, swipe }: { media: PostMedia; demo: boolean; many: boolean; state: TileState | undefined; swipe: SwipeHandlers }) {
  const [failed, setFailed] = useState<string | null>(null);
  // 弹层盖住了正文，这里不说「正文仍可阅读」；几张时提一句可以接着看别的（评审 R1 挑刺）
  const text = unavailableText(media, demo) ?? (state === "error" || failed === media.media_id ? (many ? "这张暂时读不出来，可以接着看别的。" : "这张暂时读不出来。") : null);
  return <figure className="ps-post-viewer__stage" {...swipe}>
    {text ? <p className="ps-post-viewer__note" role="status">{text}</p>
      : <img key={media.media_id} src={media.url!} alt={media.alt ?? "动态配图"} draggable={false} onError={() => setFailed(media.media_id)} />}
    {!text && media.alt ? <figcaption>{media.alt}</figcaption> : null}
  </figure>;
}
